/**
 * CustomDiffuserPage.tsx — Custom Diffuser Designer
 *
 * Design a bespoke membrane diffuser from first principles:
 *   · Choose shape, membrane material, size, pore specification
 *   · Set a TARGET bubble diameter → pore size back-calculated via Fritz (1935)
 *   · OR set pore size directly → bubble diameter forward-calculated
 *   · Side-elevation SVG shows the bubble plume cone at actual reservoir depth
 *   · Depth-profile table shows coverage radius, bubble diameter, plume area
 *     at 0%, 25%, 50%, 75%, 100% of the water column
 *   · Performance (SOTE, SOTR, AOTR, SAE) calculated from first principles
 *   · Saves to sessionStorage rb_custom_diffuser_v1
 *   · DiffuserModulePage reads this key and offers "Custom Diffuser" as a type option
 *
 * References: Fritz (1935), Clift et al. (1978), Seol et al. (2007),
 *             Metcalf & Eddy (2014 §5.5), Stenstrom & Rosso (2008)
 */

import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Wind, ArrowLeft, CheckCircle2, AlertTriangle, Info,
  Layers, RotateCcw, Circle, Gauge, Ruler, Activity,
  Sparkles, FileText, ChevronDown, ChevronUp, Wand2,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { toast } from 'sonner';

// ─── Session storage key ──────────────────────────────────────────────────────
export const CUSTOM_DIFF_KEY = 'rb_custom_diffuser_v1';

// ─── Physics constants ────────────────────────────────────────────────────────
const G           = 9.81;
const P_ATM       = 101_325;
const RHO_AIR_STD = 1.293;
const O2_MASS_FRAC= 0.2314;
const DO_SAT_20   = 9.09;
const GAMMA_AIR   = 1.4;

// ─── Physics (Kell 1975, Andrade-Vogel, Vargaftik 1983) ──────────────────────
const rho_w  = (T: number) => 999.842 + 6.793952e-2*T - 9.095290e-3*T**2 + 1.001685e-4*T**3 - 1.120083e-6*T**4;
const mu_w   = (T: number) => 2.414e-5 * Math.pow(10, 247.8/(T+133.15));
const sigma_w= (T: number) => Math.max(0.04, 0.07564 - 1.376e-4*T);
const nu_w   = (T: number) => mu_w(T)/rho_w(T);
const doSat  = (T: number) => 468/(31.6+T);

/** Fritz (1935) static bubble diameter at orifice (m) */
function fritzStaticM(poreSizeUm: number, T: number): number {
  const d_p = poreSizeUm/1e6;
  return Math.pow((6*sigma_w(T)*d_p)/(rho_w(T)*G), 1/3);
}

/** Fritz + dynamic correction → bubble diameter mm at orifice */
function bubbleDiamMm(poreSizeUm: number, qDiffNm3h: number, pores: number, T: number): number {
  const d_stat = fritzStaticM(poreSizeUm, T) * 1000; // mm
  const q_p = (qDiffNm3h/3600)/Math.max(1, pores);
  return d_stat * (1 + 0.9*Math.pow(Math.max(q_p*1e9, 0.001), 0.25));
}

/** Back-calculate pore size (µm) from target bubble diameter (mm)
 *  Inverts Fritz: d_p = d_stat^3 × ρ_w × g / (6σ)
 *  where d_stat = d_b / dyn_corr (dyn_corr computable from fixed pore count & flow)
 */
function backCalcPoreSizeUm(targetMm: number, qDiffNm3h: number, pores: number, T: number): number {
  const q_p = (qDiffNm3h/3600)/Math.max(1, pores);
  const dynCorr = 1 + 0.9*Math.pow(Math.max(q_p*1e9, 0.001), 0.25);
  const d_stat_m = (targetMm/dynCorr)/1000; // m
  const d_p = Math.pow(d_stat_m, 3) * rho_w(T) * G / (6*sigma_w(T));
  return Math.max(10, Math.min(1000, d_p*1e6));
}

/** Clift et al. (1978) bubble rise velocity m/s, temperature corrected */
function riseVel(d_mm: number, T: number): number {
  let v0: number;
  if      (d_mm <= 0.5) v0 = 0.13 + 0.16*d_mm;
  else if (d_mm <= 3.0) v0 = 0.14 + 0.065*d_mm;
  else if (d_mm <= 6.0) v0 = 0.335 + 0.02*(d_mm-3);
  else                   v0 = 0.395;
  return v0 * Math.pow(nu_w(20)/nu_w(T), 0.7);
}

/** Bubble diameter at pressure corresponding to height z above diffuser (ideal gas) */
function bubbleDiamAtHeight(d_bed_mm: number, z: number, H_eff: number, T: number): number {
  const P_bed = P_ATM + rho_w(T)*G*H_eff;  // pressure at diffuser
  const P_z   = P_ATM + rho_w(T)*G*(H_eff - z); // pressure at height z above diffuser
  return d_bed_mm * Math.pow(P_bed/Math.max(P_z, P_ATM), 1/3);
}

/** SOTE per metre from bubble diameter — calibrated to published data
 *  Stenstrom & Rosso (2008), WEF MOP 8: SOTE ≈ 10.5·e^(−0.32·d_b) %/m
 *  d_b=1.5mm→6.5%/m, d_b=2.0mm→5.5%/m, d_b=3.0mm→4.0%/m, d_b=5mm→2.5%/m
 */
function sotePerMetre(d_b_mm: number): number {
  return Math.max(1.5, Math.min(9.5, 10.5*Math.exp(-0.32*d_b_mm)));
}

/** Active membrane area from shape (m²) */
function activeAreaM2(spec: CustomDiffSpec): number {
  if (spec.shape === 'disc' || spec.shape === 'dome') {
    return (spec.activeAreaFrac/100) * Math.PI * (spec.diameterMm/2/1000)**2;
  }
  if (spec.shape === 'tube') {
    return (spec.activeAreaFrac/100) * Math.PI * (spec.diameterMm/1000) * (spec.lengthMm/1000);
  }
  // strip
  return (spec.activeAreaFrac/100) * (spec.lengthMm/1000) * (spec.widthMm/1000);
}

/** Pore count from density and active area */
function poreCount(spec: CustomDiffSpec): number {
  const A_cm2 = activeAreaM2(spec) * 1e4;
  return Math.max(1, Math.round(spec.poreDensityPerCm2 * A_cm2));
}

// ─── Interface ────────────────────────────────────────────────────────────────

export interface CustomDiffSpec {
  name:               string;
  notes:              string;

  // Geometry
  shape:              'disc' | 'dome' | 'tube' | 'strip';
  diameterMm:         number;   // disc/dome/tube outer diameter
  lengthMm:           number;   // tube/strip length
  widthMm:            number;   // strip width only
  activeAreaFrac:     number;   // % of total area that is permeable (50–95)

  // Membrane
  material:           string;   // EPDM | PTFE | Silicone | Ceramic | Polyurethane
  useBubbleTarget:    boolean;  // true → specify target bubble diam → back-calc pore size
  targetBubbleMm:     number;   // desired bubble diam at orifice (mm) — only if useBubbleTarget
  poreSizeUm:         number;   // actual pore size (µm) — only if !useBubbleTarget
  poreDensityPerCm2:  number;   // pores per cm² of active area
  membThicknessMm:    number;   // affects pressure drop

  // Operating
  airflowNm3h:        number;   // per diffuser
  waterDepthM:        number;   // total reservoir/tank depth (m)
  mountingHeightM:    number;   // frame legs height off bed (m)
  waterTempC:         number;

  // Process
  currentDoMgL:       number;
  alphaFactor:        number;
  betaFactor:         number;
  diffuserDropKpa:    number;
  blowerEff:          number;
  safetyPct:          number;

  // SOTE
  soteMode:           'calculated' | 'manual';
  manualSotePerM:     number;   // %/m when soteMode='manual'
}

const DEFAULTS: CustomDiffSpec = {
  name: 'My Custom Diffuser',
  notes: '',
  shape: 'disc', diameterMm: 300, lengthMm: 1000, widthMm: 50,
  activeAreaFrac: 75,
  material: 'EPDM',
  useBubbleTarget: true, targetBubbleMm: 1.8, poreSizeUm: 80,
  poreDensityPerCm2: 40,
  membThicknessMm: 2.5,
  airflowNm3h: 1.2,
  waterDepthM: 5.0, mountingHeightM: 0.40, waterTempC: 15,
  currentDoMgL: 2.0, alphaFactor: 0.80, betaFactor: 0.98,
  diffuserDropKpa: 1.5, blowerEff: 0.70, safetyPct: 10,
  soteMode: 'calculated', manualSotePerM: 6.5,
};

const MATERIALS = ['EPDM','PTFE','Silicone','Ceramic','Polyurethane'] as const;
const SHAPES    = ['disc','dome','tube','strip'] as const;

// ─── Small reusable components ────────────────────────────────────────────────

function NI({ label, value, onChange, min, max, step, unit, tip }:
  { label:string; value:number; onChange:(v:number)=>void; min?:number; max?:number; step?:number; unit?:string; tip?:string }) {
  return (
    <div>
      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
        {label}{unit && <span className="font-normal text-slate-600 ml-1">({unit})</span>}
      </label>
      {tip && <p className="text-[9px] text-slate-600 mb-1">{tip}</p>}
      <input type="number" value={value} step={step??0.1} min={min} max={max}
        onChange={e=>onChange(Number(e.target.value))}
        className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:border-sky-500"/>
    </div>
  );
}
function Card({ title, icon:Icon, iconColor, children }:
  { title:string; icon:React.ElementType; iconColor:string; children:React.ReactNode }) {
  return (
    <div className="rb-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className={`w-4 h-4 ${iconColor}`}/>
        <h3 className="text-sm font-bold text-slate-200">{title}</h3>
      </div>
      {children}
    </div>
  );
}
function Kpi({ label, val, unit, col, warn=false }:
  { label:string; val:string; unit:string; col:string; warn?:boolean }) {
  return (
    <div className={`rb-section px-3 py-2 relative ${warn?'border-amber-500/40':''}`}>
      <div className="text-[9px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-base font-bold font-mono" style={{color:col}}>{val}</div>
      <div className="text-[10px] text-slate-600">{unit}</div>
      {warn && <AlertTriangle className="absolute top-2 right-2 w-3 h-3 text-amber-400"/>}
    </div>
  );
}
const fmt = (v:number, dp=2) => isNaN(v)||!isFinite(v)?'—':v.toFixed(dp);

// ─── Side-Elevation SVG ───────────────────────────────────────────────────────

function ElevationSVG({ spec, R }: { spec: CustomDiffSpec; R: ReturnType<typeof computeAll> }) {
  const W = 520, H_SVG = 380, PAD_X = 60, PAD_TOP = 24, PAD_BOT = 24;
  const drawH = H_SVG - PAD_TOP - PAD_BOT;
  const drawW = W - PAD_X * 2;

  const totalDepth = spec.waterDepthM;
  const depthToScale = (d: number) => PAD_TOP + (d / totalDepth) * drawH; // 0=surface, totalDepth=bed
  const mToX = (m: number) => W/2 + m * (drawW/2) / Math.max(R.r_surface * 1.5, 0.5);

  // Key depths
  const yBed      = depthToScale(totalDepth);
  const yDiffTop  = depthToScale(totalDepth - spec.mountingHeightM);
  const ySurface  = depthToScale(0);
  const yDiffBot  = depthToScale(totalDepth - spec.mountingHeightM + 0.05); // thin disc

  const fractions = [0, 0.25, 0.50, 0.75, 1.0];
  const CONE_COL  = '#06b6d4';
  const BED_COL   = '#78350f';

  // Build plume cone points for left and right side
  const conePoints = fractions.map(f => {
    const z    = f * R.effSubm;
    const r    = R.r_diff + 0.1 * z;
    const depth= totalDepth - spec.mountingHeightM - z;
    const y    = depthToScale(Math.max(0, depth));
    return { z, r, depth, y };
  });

  const leftPts  = conePoints.map(p => `${mToX(-p.r)},${p.y}`).reverse().join(' ');
  const rightPts = conePoints.map(p => `${mToX(p.r)},${p.y}`).join(' ');
  const polyPts  = leftPts + ' ' + rightPts;

  return (
    <svg width={W} height={H_SVG} className="w-full">
      {/* Background */}
      <rect width={W} height={H_SVG} fill="transparent"/>

      {/* Water body */}
      <rect x={PAD_X} y={ySurface} width={W-PAD_X*2} height={yBed-ySurface}
        fill="#0ea5e920" stroke="none"/>

      {/* Bed hatch */}
      <rect x={PAD_X} y={yBed} width={W-PAD_X*2} height={H_SVG-yBed} fill="#78350f20"/>
      {[...Array(12)].map((_,i) => (
        <line key={i} x1={PAD_X + i*((W-PAD_X*2)/12)} y1={yBed}
          x2={PAD_X + i*((W-PAD_X*2)/12) - 14} y2={yBed+14}
          stroke={BED_COL} strokeWidth={1} opacity={0.6}/>
      ))}

      {/* Water surface */}
      <line x1={PAD_X} y1={ySurface} x2={W-PAD_X} y2={ySurface}
        stroke="#38bdf8" strokeWidth={2}/>
      <text x={PAD_X-4} y={ySurface+4} textAnchor="end" fontSize={9} fill="#38bdf8">Surface</text>

      {/* Bed line */}
      <line x1={PAD_X} y1={yBed} x2={W-PAD_X} y2={yBed}
        stroke={BED_COL} strokeWidth={2}/>
      <text x={PAD_X-4} y={yBed-2} textAnchor="end" fontSize={9} fill="#d97706">Bed</text>

      {/* Depth markers (25%, 50%, 75%) */}
      {[0.25, 0.50, 0.75].map(f => {
        const y = depthToScale(f*totalDepth);
        return (
          <g key={f}>
            <line x1={PAD_X} y1={y} x2={W-PAD_X} y2={y}
              stroke="#1e293b" strokeWidth={1} strokeDasharray="4,3"/>
            <text x={W-PAD_X+4} y={y+3} fontSize={8} fill="#475569">{fmt(f*totalDepth,1)}m</text>
          </g>
        );
      })}

      {/* Plume cone (filled) */}
      <polygon points={polyPts} fill={`${CONE_COL}18`} stroke={CONE_COL} strokeWidth={1.5}
        strokeDasharray="5,3" opacity={0.8}/>

      {/* Diffuser disc */}
      <rect x={mToX(-R.r_diff)} y={yDiffTop-3} width={mToX(R.r_diff)-mToX(-R.r_diff)} height={6}
        rx={2} fill="#4c1d95" stroke="#7c3aed" strokeWidth={1.5}/>
      <text x={W/2} y={yDiffTop+14} textAnchor="middle" fontSize={8} fill="#a78bfa">
        Diffuser ∅{spec.diameterMm}mm
      </text>

      {/* Mounting height brace */}
      <line x1={PAD_X+8} y1={yBed} x2={PAD_X+8} y2={yDiffTop}
        stroke="#d97706" strokeWidth={1} strokeDasharray="2,2"/>
      <text x={PAD_X+12} y={(yBed+yDiffTop)/2+4} fontSize={8} fill="#d97706">
        h={spec.mountingHeightM}m
      </text>

      {/* Upward air arrows */}
      {[-0.5, 0, 0.5].map((off, i) => {
        const x = mToX(off * R.r_diff);
        const yTop = yDiffTop - 28;
        return (
          <g key={i}>
            <line x1={x} y1={yDiffTop-4} x2={x} y2={yTop+6} stroke="#a78bfa" strokeWidth={1.5}/>
            <polygon points={`${x},${yTop} ${x-4},${yTop+8} ${x+4},${yTop+8}`} fill="#a78bfa"/>
          </g>
        );
      })}

      {/* Bubble circles at 0%, 25%, 50%, 75%, 100% of depth */}
      {fractions.map((f, i) => {
        const z     = f * R.effSubm;
        const d_z   = R.depthProfile[i]?.bubbleMm ?? R.d_b_mm;
        const r_px  = Math.max(2, d_z * 3); // visual size in px
        const depth = totalDepth - spec.mountingHeightM - z;
        const y     = depthToScale(Math.max(0, depth));
        const x     = mToX(R.r_diff + 0.1 * z) + 8 + r_px;
        return (
          <g key={f}>
            <circle cx={x} cy={y} r={r_px} fill="#8b5cf640" stroke="#8b5cf6" strokeWidth={1}/>
            <text x={x + r_px + 3} y={y + 3} fontSize={7} fill="#8b5cf6">
              {fmt(d_z, 2)}mm
            </text>
          </g>
        );
      })}

      {/* Coverage radius annotations */}
      {/* At surface */}
      <line x1={W/2} y1={ySurface-4} x2={mToX(R.r_surface)} y2={ySurface-4}
        stroke="#06b6d4" strokeWidth={1} markerEnd="url(#arr)"/>
      <text x={(W/2+mToX(R.r_surface))/2} y={ySurface-7} textAnchor="middle" fontSize={8} fill="#06b6d4">
        r={fmt(R.r_surface,2)}m (surface)
      </text>

      {/* At diffuser level — influence zone */}
      <line x1={W/2} y1={yDiffTop} x2={mToX(R.r_inf_bed)} y2={yDiffTop}
        stroke="#f59e0b" strokeWidth={1} strokeDasharray="3,2"/>
      <text x={(W/2+mToX(R.r_inf_bed))/2} y={yDiffTop-5} textAnchor="middle" fontSize={8} fill="#f59e0b">
        r_inf={fmt(R.r_inf_bed,2)}m
      </text>

      {/* r_diff (physical disc) */}
      <text x={mToX(R.r_diff)+4} y={yDiffTop+22} fontSize={7} fill="#7c3aed">
        r_d={fmt(R.r_diff,3)}m
      </text>

      {/* Labels */}
      <text x={W/2} y={14} textAnchor="middle" fontSize={9} fontWeight="bold" fill="#94a3b8">
        Side Elevation — Bubble Plume Cone (not to scale horizontally)
      </text>
      <text x={W-PAD_X+4} y={ySurface+4} fontSize={9} fill="#38bdf8">
        H={spec.waterDepthM}m
      </text>
    </svg>
  );
}

// ─── Main computation ─────────────────────────────────────────────────────────

function computeAll(spec: CustomDiffSpec) {
  const T       = spec.waterTempC;
  const A_m2    = activeAreaM2(spec);
  const N_pores = poreCount(spec);
  const effSubm = Math.max(0.5, spec.waterDepthM - spec.mountingHeightM);

  // Pore size resolution: target bubble → back-calc pore size, or direct
  const resolvedPoreSizeUm = spec.useBubbleTarget
    ? backCalcPoreSizeUm(spec.targetBubbleMm, spec.airflowNm3h, N_pores, T)
    : spec.poreSizeUm;

  // Bubble diameter at orifice (bed level)
  const d_b_mm = bubbleDiamMm(resolvedPoreSizeUm, spec.airflowNm3h, N_pores, T);

  // Physical diffuser radius
  let r_diff: number;
  if (spec.shape === 'disc' || spec.shape === 'dome') {
    r_diff = spec.diameterMm/2/1000;
  } else if (spec.shape === 'tube') {
    r_diff = spec.diameterMm/2/1000;  // tube radius
  } else {
    r_diff = Math.max(spec.lengthMm, spec.widthMm)/2/1000; // strip half-width
  }

  // Plume surface radius: r_diff + 0.1 × H_eff (Seol 2007, half-angle 5.7°)
  const r_surface = r_diff + 0.1 * effSubm;

  // Influence radius at bed level (horizontal induced velocity zone)
  // From Seol 2007: plume-induced horizontal radius ≈ 0.35 × H_eff at bed
  const r_inf_bed = Math.max(r_diff, 0.35 * effSubm);

  // SOTE per metre (from bubble diam or manual)
  const soteM = spec.soteMode === 'manual'
    ? spec.manualSotePerM
    : sotePerMetre(d_b_mm);
  const sotePct = Math.min(99, soteM * effSubm);

  // O₂ transfer
  const P_bed    = P_ATM + rho_w(T)*G*effSubm;
  const q_act    = (spec.airflowNm3h/3600) * (P_ATM/P_bed);
  const modSOTR  = spec.airflowNm3h * RHO_AIR_STD * O2_MASS_FRAC * sotePct/100;
  const cs_T     = doSat(T);
  const theta    = Math.pow(1.024, T-20);
  const deficit  = Math.max(0, cs_T - spec.currentDoMgL);
  const modAOTR  = modSOTR * spec.alphaFactor * spec.betaFactor * theta * deficit / DO_SAT_20;

  // Blower power (single diffuser basis)
  const P_out = P_ATM + (rho_w(T)*G*effSubm + spec.diffuserDropKpa*1000)*(1+spec.safetyPct/100);
  const exp   = (GAMMA_AIR-1)/GAMMA_AIR;
  const blowerKw = (GAMMA_AIR/(GAMMA_AIR-1)) * P_ATM * (spec.airflowNm3h/3600) *
    (Math.pow(P_out/P_ATM, exp)-1) / spec.blowerEff / 1000;
  const SAE = blowerKw > 0 ? modAOTR/blowerKw : 0;

  // Depth profile — 5 levels (0%, 25%, 50%, 75%, 100% of effSubm)
  const frac = [0, 0.25, 0.5, 0.75, 1.0];
  const depthProfile = frac.map(f => {
    const z         = f * effSubm;
    const bubbleMm  = bubbleDiamAtHeight(d_b_mm, z, effSubm, T);
    const riseMs    = riseVel(bubbleMm, T);
    const r_plume   = r_diff + 0.1*z;
    const areaM2    = Math.PI * r_plume**2;
    const depth_m   = spec.waterDepthM - spec.mountingHeightM - z; // below surface
    return { frac:f, z, depthBelowSurface: Math.max(0, depth_m), bubbleMm, riseMs, r_plume, areaM2 };
  });

  // Rise time (mean of bed and surface velocity)
  const v_mean = (riseVel(d_b_mm, T) + riseVel(depthProfile[4].bubbleMm, T))/2;
  const riseTimeS = effSubm / v_mean;

  // Physical pressure drop across membrane (simplified Darcy-Weisbach for porous media)
  const pressDropMem = spec.membThicknessMm * 0.05 * (spec.airflowNm3h / N_pores); // rough estimate kPa

  // Bed velocity & Shields (simplified for single diffuser — informational)
  const B_buoy = G * q_act;
  const U_plume = 1.46 * Math.pow(B_buoy * effSubm, 1/3);
  const v_bed  = 0.15 * U_plume / Math.sqrt(Math.max(effSubm, 1));

  // Coverage areas - TYPE-AWARE
  // DISC/DOME (point source): circular  -> A = π·r²
  // TUBE (line source):        rectangular curtain -> A = (L + 2r) × 2r
  // STRIP (planar source):      rectangular         -> A = (L + 2r) × (W + 2r)
  let coverageSurfM2: number, coverageBedM2: number, physicalAreaM2: number;
  if (spec.shape === 'disc' || spec.shape === 'dome') {
    // Point source - cone footprint
    coverageSurfM2 = Math.PI * r_surface ** 2;
    coverageBedM2  = Math.PI * r_inf_bed ** 2;
    physicalAreaM2 = Math.PI * r_diff ** 2;
  } else if (spec.shape === 'tube') {
    // Line source - planar curtain footprint
    const L = spec.lengthMm / 1000;
    coverageSurfM2 = (L + 2 * r_surface) * (2 * r_surface);
    coverageBedM2  = (L + 2 * r_inf_bed) * (2 * r_inf_bed);
    physicalAreaM2 = Math.PI * (spec.diameterMm / 1000) * L; // cylindrical lateral surface
  } else {
    // Strip - rectangular plane source
    const L = spec.lengthMm / 1000;
    const W = spec.widthMm / 1000;
    coverageSurfM2 = (L + 2 * r_surface) * (W + 2 * r_surface);
    coverageBedM2  = (L + 2 * r_inf_bed) * (W + 2 * r_inf_bed);
    physicalAreaM2 = L * W; // strip top area
  }

  // Suggestions
  const bubbleOk  = d_b_mm <= 3.0;
  const soteSug   = soteM >= 5.0;
  const areaOk    = A_m2 >= 0.01;
  const poreOk    = resolvedPoreSizeUm >= 30 && resolvedPoreSizeUm <= 300;

  return {
    A_m2, N_pores, effSubm, resolvedPoreSizeUm,
    d_b_mm, r_diff, r_surface, r_inf_bed,
    soteM, sotePct, modSOTR, modAOTR, SAE, blowerKw,
    cs_T, theta, deficit,
    depthProfile, riseTimeS, v_mean, v_bed, pressDropMem,
    coverageSurfM2, coverageBedM2, physicalAreaM2,
    bubbleOk, soteSug, areaOk, poreOk,
  };
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CustomDiffuserPage() {
  const navigate = useNavigate();

  const [spec, setSpec] = useState<CustomDiffSpec>(() => {
    try {
      const raw = sessionStorage.getItem(CUSTOM_DIFF_KEY);
      return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : DEFAULTS;
    } catch { return DEFAULTS; }
  });

  const upd = (p: Partial<CustomDiffSpec>) => setSpec(prev => ({ ...prev, ...p }));

  useEffect(() => {
    try { sessionStorage.setItem(CUSTOM_DIFF_KEY, JSON.stringify(spec)); } catch {}
  }, [spec]);

  const R = useMemo(() => computeAll(spec), [spec]);

  // Build the record that DiffuserModulePage will read
  const exportRecord = {
    value:       'custom',
    label:       spec.name || 'Custom Diffuser',
    sote:        R.soteM,                    // %/m — used by DiffuserModulePage
    poreSize:    R.resolvedPoreSizeUm,       // µm
    diameterMm:  spec.diameterMm,
    activePores: R.N_pores,                  // used directly (bypasses disc-area formula)
    shape:       spec.shape,
    alpha:       spec.alphaFactor,
    beta:        spec.betaFactor,
    r_diff:      R.r_diff,                   // m — physical radius (or half-width for line/strip)
    r_surface:   R.r_surface,               // m — surface plume halfwidth/radius
    r_inf_bed:   R.r_inf_bed,               // m — bed influence halfwidth/radius
    // Tube/strip extras — used by downstream pages to render correct geometry
    lengthMm:    spec.lengthMm,
    widthMm:     spec.widthMm,
    coverageBedM2:  R.coverageBedM2,        // m² — TYPE-CORRECT footprint
    coverageSurfM2: R.coverageSurfM2,
    notes: `${spec.material} ${spec.shape} ${spec.shape === 'tube' || spec.shape === 'strip'
      ? `${spec.lengthMm}×${spec.shape === 'strip' ? spec.widthMm : 'Ø' + spec.diameterMm} mm`
      : `∅${spec.diameterMm}mm`} · ${fmt(R.resolvedPoreSizeUm,0)}µm pores · d_b=${fmt(R.d_b_mm,2)}mm · SOTE ${fmt(R.soteM,1)}%/m`,
  };

  // Save export record under a second key so DiffuserModulePage can detect it fast
  // Mirrored to localStorage via persistence layer so all pages see it across sessions
  useEffect(() => {
    try {
      sessionStorage.setItem('rb_custom_diffuser_export_v1', JSON.stringify(exportRecord));
      localStorage.setItem('rb_custom_diffuser_export_v1', JSON.stringify(exportRecord));
      window.dispatchEvent(new CustomEvent('rb-persist-changed', {
        detail: { key: 'rb_custom_diffuser_export_v1', value: exportRecord }
      }));
    } catch {}
  }, [spec, R.soteM, R.resolvedPoreSizeUm, R.N_pores, R.r_diff, R.r_surface, R.r_inf_bed]);

  const [showSuggestions, setShowSuggestions] = useState(true);

  return (
    <div className="min-h-screen bg-rb-bg p-4 md:p-6 space-y-5">

      <PageHeader
        icon={Sparkles}
        iconColor="text-violet-400"
        title="Custom Diffuser Designer"
        subtitle="Design your own membrane diffuser · set target bubble size · see coverage depth-profile"
        badge={
          <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-violet-500/10 text-violet-400 border border-violet-800/40">
            {spec.shape} · {fmt(R.d_b_mm, 2)} mm bubble
          </span>
        }
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => navigate('/diffuser-module')}
              className="px-3 py-1.5 text-xs font-bold text-cyan-300 bg-rb-elevated border border-cyan-700/60 hover:border-cyan-500 rounded-lg flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5"/> Use in Module Design
            </button>
            <button onClick={() => navigate('/diffuser-validation')}
              className="px-3 py-1.5 text-xs font-bold text-sky-300 bg-rb-elevated border border-sky-700/60 hover:border-sky-500 rounded-lg flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5"/> Validation Report
            </button>
            <button onClick={() => { setSpec(DEFAULTS); toast.info('Reset to defaults'); }}
              className="px-3 py-1.5 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5"/> Reset
            </button>
            <button onClick={() => navigate(-1)}
              className="px-3 py-1.5 text-xs font-bold text-slate-400 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg flex items-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5"/> Back
            </button>
          </div>
        }
      />

      {/* Integration status banner */}
      <div className="p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-[11px] text-emerald-200/70 flex items-center gap-3 flex-wrap">
        <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0"/>
        <span>
          Custom diffuser <strong className="text-emerald-300">"{spec.name}"</strong> is saved and active.
          Go to <button onClick={()=>navigate('/diffuser-module')} className="underline text-emerald-400 hover:text-emerald-300">Diffuser Module</button> and
          select <strong className="text-emerald-300">Custom Diffuser</strong> from the type list to use these settings in all array calculations.
        </span>
        <span className="ml-auto text-[10px] font-mono text-emerald-500">
          SOTE {fmt(R.soteM,1)}%/m · d_b {fmt(R.d_b_mm,2)}mm · {R.N_pores} pores
        </span>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi label="Bubble ∅ (orifice)"  val={fmt(R.d_b_mm,3)}   unit="mm at diffuser"   col="#8b5cf6" warn={R.d_b_mm>3.0}/>
        <Kpi label="Bubble ∅ (surface)"  val={fmt(R.depthProfile[4].bubbleMm,3)} unit="mm expanded" col="#a78bfa"/>
        <Kpi label="Pore size"            val={fmt(R.resolvedPoreSizeUm,1)} unit="µm resolved" col="#06b6d4" warn={!R.poreOk}/>
        <Kpi label="Active pores"         val={String(R.N_pores)} unit={`@ ${spec.poreDensityPerCm2}/cm²`} col="#38bdf8"/>
        <Kpi label="SOTE / metre"         val={fmt(R.soteM,2)}    unit="%/m submergence"  col="#10b981" warn={!R.soteSug}/>
        <Kpi label="AOTR / diffuser"      val={fmt(R.modAOTR,4)}  unit="kgO₂/h"          col="#f59e0b"/>
        <Kpi label="Surface plume r"      val={fmt(R.r_surface,3)} unit="m radius"        col="#06b6d4"/>
        <Kpi label="SAE"                  val={fmt(R.SAE,2)}       unit="kgO₂/kWh"       col={R.SAE>=1.5?'#10b981':'#ef4444'} warn={R.SAE<1.2}/>
      </div>

      {/* Main layout */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">

        {/* LEFT: SVG + depth profile (3/5) */}
        <div className="xl:col-span-3 space-y-4">

          {/* Side elevation */}
          <Card title="Side Elevation — Bubble Plume & Coverage Depth Profile" icon={Activity} iconColor="text-cyan-400">
            <ElevationSVG spec={spec} R={R}/>
            <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] text-slate-500">
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-cyan-500/30 border border-cyan-500 inline-block"/>
                Plume cone (Seol 2007 — half-angle 5.7°)
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-violet-500/30 border border-violet-500 inline-block"/>
                Bubble size at height (Fritz + ideal gas)
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-3 h-3 inline-block" style={{background:'#f59e0b22',border:'1px dashed #f59e0b'}}/>
                Bed influence radius r_inf
              </div>
            </div>
          </Card>

          {/* Depth profile table */}
          <Card title="Coverage Depth Profile — Per Single Diffuser" icon={Ruler} iconColor="text-amber-400">
            <div className="text-[10px] text-slate-500 mb-2">
              Plume cone: r(z) = r_diff + tan(5.7°)×z = {fmt(R.r_diff,3)} + 0.1z &nbsp;|&nbsp;
              Bubble expansion: ideal gas (P_bed/P(z))^(1/3) &nbsp;|&nbsp;
              Rise velocity: Clift et al. (1978) + ν temperature correction
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[10px] border-collapse">
                <thead>
                  <tr className="border-b border-slate-700/60">
                    {['Depth level','Height above diff (m)','Depth below surface (m)','Bubble ∅ (mm)','Rise vel (m/s)','Plume radius (m)','Plume area (m²)','Note'].map(h => (
                      <th key={h} className="py-1.5 pr-3 text-[9px] font-bold text-slate-500 uppercase whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {R.depthProfile.map((row, i) => (
                    <tr key={i} className={`border-b border-slate-800/30 ${i===0?'bg-violet-500/5':i===4?'bg-cyan-500/5':''}`}>
                      <td className="py-2 pr-3 font-bold text-slate-300">{['At diffuser','25% up','50% up','75% up','At surface'][i]}</td>
                      <td className="py-2 pr-3 font-mono text-slate-100">{fmt(row.z,2)}</td>
                      <td className="py-2 pr-3 font-mono text-slate-100">{fmt(row.depthBelowSurface,2)}</td>
                      <td className="py-2 pr-3 font-mono text-violet-300">{fmt(row.bubbleMm,3)}</td>
                      <td className="py-2 pr-3 font-mono text-slate-300">{fmt(row.riseMs,4)}</td>
                      <td className="py-2 pr-3 font-mono text-cyan-300">{fmt(row.r_plume,3)}</td>
                      <td className="py-2 pr-3 font-mono text-emerald-300">{fmt(row.areaM2,4)}</td>
                      <td className="py-2 pr-3 text-slate-600 italic">
                        {i===0?'Detachment':i===1?'1st quarter':i===2?'Half depth':i===3?'Near surface':'Water surface'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Coverage summary */}
            <div className="mt-3 grid grid-cols-3 gap-3 p-3 rounded-xl bg-rb-elevated border border-slate-800/50">
              <div>
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">
                  {spec.shape === 'tube' ? 'Tube lateral surface' :
                   spec.shape === 'strip' ? 'Strip top area' : 'Physical disc area'}
                </div>
                <div className="font-mono font-bold text-violet-300">{fmt(R.physicalAreaM2*1e4,2)} cm²</div>
                <div className="text-[9px] text-slate-600">{fmt(R.physicalAreaM2,5)} m²</div>
              </div>
              <div>
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">
                  Surface plume area
                  <span className="ml-1 text-violet-400 font-normal">
                    ({spec.shape === 'tube' ? 'rectangular curtain' :
                      spec.shape === 'strip' ? 'rectangular plane' : 'circular cone'})
                  </span>
                </div>
                <div className="font-mono font-bold text-cyan-300">{fmt(R.coverageSurfM2,4)} m²</div>
                <div className="text-[9px] text-slate-600">
                  {spec.shape === 'tube' || spec.shape === 'strip'
                    ? `${fmt(R.r_surface,2)} m halfwidth at waterline`
                    : `r=${fmt(R.r_surface,3)} m at waterline`}
                </div>
              </div>
              <div>
                <div className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">
                  Bed influence area
                  <span className="ml-1 text-violet-400 font-normal">
                    ({spec.shape === 'tube' || spec.shape === 'strip' ? 'Wüest 1992' : 'Seol 2007'})
                  </span>
                </div>
                <div className="font-mono font-bold text-amber-300">{fmt(R.coverageBedM2,3)} m²</div>
                <div className="text-[9px] text-slate-600">r_inf={fmt(R.r_inf_bed,3)} m</div>
              </div>
            </div>
          </Card>

          {/* Design suggestions */}
          <Card title="Design Suggestions & Optimisation" icon={Wand2} iconColor="text-emerald-400">
            <button onClick={()=>setShowSuggestions(p=>!p)}
              className="flex items-center gap-2 text-[10px] font-bold text-slate-400 mb-3 hover:text-slate-300">
              {showSuggestions ? <ChevronUp className="w-3.5 h-3.5"/> : <ChevronDown className="w-3.5 h-3.5"/>}
              {showSuggestions ? 'Hide' : 'Show'} Recommendations
            </button>
            {showSuggestions && (
              <div className="space-y-2 text-[11px] text-slate-400">

                {/* Bubble size recommendation */}
                <div className={`p-3 rounded-xl border ${R.bubbleOk?'bg-emerald-500/5 border-emerald-500/20':'bg-amber-500/5 border-amber-500/20'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {R.bubbleOk ? <CheckCircle2 className="w-4 h-4 text-emerald-400"/> : <AlertTriangle className="w-4 h-4 text-amber-400"/>}
                    <strong className={R.bubbleOk?'text-emerald-300':'text-amber-300'}>
                      Bubble diameter: {fmt(R.d_b_mm,2)} mm {R.bubbleOk?'— fine bubble ✓':'— consider smaller pores for finer bubbles'}
                    </strong>
                  </div>
                  <p>Fine bubble (&lt;3 mm) maximises oxygen transfer per unit air volume. Target 1.5–2.5 mm for EPDM membranes.
                  Use &lt;80 µm pores for this range. Current pore size: {fmt(R.resolvedPoreSizeUm,0)} µm
                  {R.resolvedPoreSizeUm > 100 ? ' — reduce pore size or use PTFE membrane for finer bubbles.' : ' — good for fine bubble classification.'}</p>
                </div>

                {/* Orientation */}
                <div className="p-3 rounded-xl bg-sky-500/5 border border-sky-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Info className="w-4 h-4 text-sky-400"/>
                    <strong className="text-sky-300">Diffuser Orientation: Always upward-facing</strong>
                  </div>
                  <p>The {spec.shape} diffuser must be mounted with the permeable membrane face pointing
                  <strong className="text-sky-200"> directly upward</strong>. Air exits vertically, bubbles
                  rise under buoyancy in a straight column. Tilting &gt;15° from vertical reduces OTE
                  by 10–25% (Loubière &amp; Hébrard 2003) and risks membrane contact with disturbed bed sediment.
                  Maintain at least {spec.mountingHeightM} m clearance above the bed.</p>
                </div>

                {/* Spacing between diffusers */}
                <div className="p-3 rounded-xl bg-violet-500/5 border border-violet-500/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Ruler className="w-4 h-4 text-violet-400"/>
                    <strong className="text-violet-300">Spacing Between Diffusers</strong>
                  </div>
                  <p>Diffusers should be spaced so adjacent plume cones just touch at mid-depth
                  — not so close that they overlap and create turbulent interference (Bhatt et al. 2011)
                  and not so far apart that coverage gaps exist.
                  Recommended centre-to-centre spacing: <strong className="text-violet-200">{fmt(2*R.r_surface * 0.9, 2)} – {fmt(2*R.r_surface * 1.1,2)} m</strong>
                  &nbsp;(= 0.9–1.1 × 2r_surface = {fmt(2*R.r_surface,2)} m diameter plume footprint).
                  Current plume surface radius: {fmt(R.r_surface,3)} m.
                  Minimum clearance: &gt;{fmt(R.r_diff*2+0.05,2)} m (disc diameter + 50 mm).</p>
                </div>

                {/* Pore density */}
                <div className={`p-3 rounded-xl border ${R.poreOk?'bg-emerald-500/5 border-emerald-500/20':'bg-red-500/5 border-red-500/20'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {R.poreOk ? <CheckCircle2 className="w-4 h-4 text-emerald-400"/> : <AlertTriangle className="w-4 h-4 text-red-400"/>}
                    <strong className={R.poreOk?'text-emerald-300':'text-red-300'}>
                      Pore Size: {fmt(R.resolvedPoreSizeUm,1)} µm {R.poreOk?'— within fine-bubble range':'— outside recommended range'}
                    </strong>
                  </div>
                  <p>Fine-bubble membranes: 40–150 µm (EPDM/PTFE). Ceramic dome: 100–400 µm.
                  Below 30 µm: excessive fouling and clogging risk. Above 300 µm: coarse bubble,
                  OTE penalty. Active pores: {R.N_pores} (= {spec.poreDensityPerCm2} pores/cm² × {fmt(R.A_m2*1e4,2)} cm²).
                  Flow per pore: {(spec.airflowNm3h/3600/R.N_pores*1e9).toFixed(3)} nL/s.</p>
                </div>

                {/* SOTE */}
                <div className={`p-3 rounded-xl border ${R.soteSug?'bg-emerald-500/5 border-emerald-500/20':'bg-amber-500/5 border-amber-500/20'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {R.soteSug ? <CheckCircle2 className="w-4 h-4 text-emerald-400"/> : <AlertTriangle className="w-4 h-4 text-amber-400"/>}
                    <strong className={R.soteSug?'text-emerald-300':'text-amber-300'}>
                      SOTE: {fmt(R.soteM,2)}%/m → {fmt(R.sotePct,1)}% total at {fmt(R.effSubm,1)} m depth
                    </strong>
                  </div>
                  <p>Fine-bubble membranes achieve 5–9%/m SOTE (Stenstrom &amp; Rosso 2008).
                  SOTE increases with submergence depth — deeper installation improves efficiency.
                  At {fmt(R.effSubm,1)} m submergence: total SOTE = {fmt(R.sotePct,1)}%.
                  {R.soteM < 5 ? ' Increase submergence depth or reduce bubble size to improve OTE.' : ''}
                  SAE of {fmt(R.SAE,2)} kgO₂/kWh — target ≥ 2.5 for fine-bubble systems.</p>
                </div>

                {/* Depth effect on radius */}
                <div className="p-3 rounded-xl bg-slate-800/30 border border-slate-700/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Activity className="w-4 h-4 text-orange-400"/>
                    <strong className="text-orange-300">Effect of Depth on Coverage Radius</strong>
                  </div>
                  <p>At this depth ({spec.waterDepthM} m, H_eff={fmt(R.effSubm,1)} m):
                  plume grows from r={fmt(R.r_diff,3)} m (disc edge) to r={fmt(R.r_surface,3)} m at surface.
                  Increasing water depth increases plume spread, SOTE, and rise time — all beneficial.
                  However, bed influence radius scales as ~0.35×H — deeper water moves more sediment per diffuser.
                  Design gap between diffusers based on surface plume radius ({fmt(R.r_surface,2)} m) for full column coverage,
                  or bed influence radius ({fmt(R.r_inf_bed,2)} m) for full sediment control.</p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* RIGHT: Inputs (2/5) */}
        <div className="xl:col-span-2 space-y-4">

          {/* Identity */}
          <Card title="Diffuser Identity" icon={Sparkles} iconColor="text-violet-400">
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Name</label>
                <input value={spec.name} onChange={e=>upd({name:e.target.value})}
                  className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-sky-500"/>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Notes / description</label>
                <textarea value={spec.notes} onChange={e=>upd({notes:e.target.value})} rows={2}
                  className="w-full bg-rb-elevated border border-slate-700/60 rounded-xl px-3 py-2 text-xs text-slate-400 focus:outline-none focus:border-sky-500 resize-none"/>
              </div>
            </div>
          </Card>

          {/* Geometry */}
          <Card title="§1 — Geometry" icon={Ruler} iconColor="text-orange-400">
            <div className="mb-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Shape</div>
              <div className="grid grid-cols-2 gap-2">
                {SHAPES.map(s => (
                  <button key={s} onClick={()=>upd({shape:s})}
                    className={`py-2 rounded-xl text-xs font-bold border transition-all capitalize ${
                      spec.shape===s
                        ? 'bg-orange-500/15 border-orange-500/60 text-orange-300'
                        : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                    }`}>{s}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {(spec.shape==='disc'||spec.shape==='dome'||spec.shape==='tube') && (
                <NI label={spec.shape==='tube'?'Outer diameter':'Disc diameter'} unit="mm"
                  value={spec.diameterMm} onChange={v=>upd({diameterMm:Math.max(50,v)})}
                  step={10} min={50} max={1000}
                  tip="Outer diameter of the membrane element"/>
              )}
              {spec.shape==='tube' && (
                <NI label="Tube length" unit="mm" value={spec.lengthMm}
                  onChange={v=>upd({lengthMm:Math.max(100,v)})} step={50} min={100} max={3000}/>
              )}
              {spec.shape==='strip' && (
                <>
                  <NI label="Strip length" unit="mm" value={spec.lengthMm}
                    onChange={v=>upd({lengthMm:Math.max(100,v)})} step={50} min={100} max={3000}/>
                  <NI label="Strip width" unit="mm" value={spec.widthMm}
                    onChange={v=>upd({widthMm:Math.max(20,v)})} step={5} min={20} max={300}/>
                </>
              )}
              <NI label="Active area" unit="%" value={spec.activeAreaFrac}
                onChange={v=>upd({activeAreaFrac:Math.min(98,Math.max(30,v))})} step={5} min={30} max={98}
                tip="% of total surface that is permeable (excludes saddle, ribs, etc.)"/>
            </div>
            <div className="mt-3 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-[10px] text-slate-500 grid grid-cols-2 gap-1.5">
              <div>Total area: <strong className="text-slate-300 font-mono">
                {spec.shape==='disc'||spec.shape==='dome' ? fmt(Math.PI*(spec.diameterMm/2/1000)**2*1e4,2)
                 : spec.shape==='tube' ? fmt(Math.PI*(spec.diameterMm/1000)*(spec.lengthMm/1000)*1e4,2)
                 : fmt((spec.lengthMm/1000)*(spec.widthMm/1000)*1e4,2)} cm²
              </strong></div>
              <div>Active area: <strong className="text-orange-300 font-mono">{fmt(R.A_m2*1e4,2)} cm²</strong></div>
              <div>Physical radius r_diff: <strong className="text-violet-300 font-mono">{fmt(R.r_diff,3)} m</strong></div>
              <div>Disc area: <strong className="text-slate-300 font-mono">{fmt(R.physicalAreaM2*1e4,2)} cm²</strong></div>
            </div>
          </Card>

          {/* Membrane */}
          <Card title="§2 — Membrane Specification" icon={Circle} iconColor="text-violet-400">
            <div className="mb-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Material</div>
              <div className="flex gap-2 flex-wrap">
                {MATERIALS.map(m => (
                  <button key={m} onClick={()=>upd({material:m})}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                      spec.material===m
                        ? 'bg-violet-500/15 border-violet-500/60 text-violet-300'
                        : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                    }`}>{m}</button>
                ))}
              </div>
            </div>

            {/* Bubble target mode */}
            <div className="mb-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Pore Specification Mode</div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={()=>upd({useBubbleTarget:true})}
                  className={`py-2 rounded-xl text-[11px] font-bold border transition-all ${
                    spec.useBubbleTarget
                      ? 'bg-emerald-500/15 border-emerald-500/60 text-emerald-300'
                      : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                  }`}>
                  <Wand2 className="w-3.5 h-3.5 inline mr-1"/>Target bubble size
                </button>
                <button onClick={()=>upd({useBubbleTarget:false})}
                  className={`py-2 rounded-xl text-[11px] font-bold border transition-all ${
                    !spec.useBubbleTarget
                      ? 'bg-sky-500/15 border-sky-500/60 text-sky-300'
                      : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                  }`}>
                  Direct pore size
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {spec.useBubbleTarget ? (
                <NI label="Target bubble ∅" unit="mm" value={spec.targetBubbleMm}
                  onChange={v=>upd({targetBubbleMm:Math.max(0.5,Math.min(8,v))})} step={0.1} min={0.5} max={8}
                  tip="Desired bubble diameter at orifice — pore size is back-calculated via Fritz (1935)"/>
              ) : (
                <NI label="Pore size" unit="µm" value={spec.poreSizeUm}
                  onChange={v=>upd({poreSizeUm:Math.max(10,Math.min(1000,v))})} step={5} min={10} max={1000}
                  tip="Direct specification of membrane pore diameter"/>
              )}
              <NI label="Pore density" unit="pores/cm²" value={spec.poreDensityPerCm2}
                onChange={v=>upd({poreDensityPerCm2:Math.max(1,v)})} step={5} min={1} max={200}
                tip="Pores per cm² of active membrane area. EPDM typical: 30–60/cm²"/>
              <NI label="Membrane thickness" unit="mm" value={spec.membThicknessMm}
                onChange={v=>upd({membThicknessMm:Math.max(0.5,v)})} step={0.5} min={0.5} max={10}
                tip="Thickness affects pressure drop and structural rigidity"/>
            </div>

            <div className="mt-3 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-[10px] text-slate-500 space-y-1">
              <div className="flex justify-between">
                <span>Resolved pore size:</span>
                <strong className={`font-mono ${R.poreOk?'text-emerald-400':'text-amber-400'}`}>{fmt(R.resolvedPoreSizeUm,1)} µm</strong>
              </div>
              <div className="flex justify-between">
                <span>Active pores:</span>
                <strong className="font-mono text-slate-200">{R.N_pores.toLocaleString()}</strong>
              </div>
              <div className="flex justify-between">
                <span>Flow per pore:</span>
                <strong className="font-mono text-slate-200">{fmt(spec.airflowNm3h/3600/R.N_pores*1e9,3)} nL/s</strong>
              </div>
              <div className="flex justify-between">
                <span>Computed bubble ∅:</span>
                <strong className={`font-mono ${R.d_b_mm<=3?'text-emerald-400':R.d_b_mm<=5?'text-amber-400':'text-red-400'}`}>{fmt(R.d_b_mm,3)} mm</strong>
              </div>
            </div>
          </Card>

          {/* Operating conditions */}
          <Card title="§3 — Operating Conditions" icon={Gauge} iconColor="text-sky-400">
            <div className="grid grid-cols-2 gap-3">
              <NI label="Air flow / diffuser" unit="Nm³/h" value={spec.airflowNm3h}
                onChange={v=>upd({airflowNm3h:Math.max(0.1,v)})} step={0.1} min={0.1} max={10}
                tip="Normal m³/h per diffuser. Typical: 0.5–2.5 Nm³/h"/>
              <NI label="Water depth" unit="m" value={spec.waterDepthM}
                onChange={v=>upd({waterDepthM:Math.max(1,v)})} step={0.5} min={1} max={30}
                tip="Total depth of reservoir or tank"/>
              <NI label="Mounting height" unit="m" value={spec.mountingHeightM}
                onChange={v=>upd({mountingHeightM:Math.max(0.1,v)})} step={0.05} min={0.1} max={2.0}
                tip="Frame/saddle height off bed. Optimal: 0.30–0.50 m"/>
              <NI label="Temperature" unit="°C" value={spec.waterTempC}
                onChange={v=>upd({waterTempC:Math.max(0,Math.min(40,v))})} step={1} min={0} max={40}/>
              <NI label="Current DO" unit="mg/L" value={spec.currentDoMgL}
                onChange={v=>upd({currentDoMgL:Math.max(0,Math.min(14,v))})} step={0.1} min={0} max={14}/>
              <NI label="Diffuser pressure drop" unit="kPa" value={spec.diffuserDropKpa}
                onChange={v=>upd({diffuserDropKpa:Math.max(0,v)})} step={0.25} min={0} max={20}
                tip="Pressure loss through membrane at design flow"/>
            </div>
            <div className="mt-3 p-3 rounded-xl bg-rb-elevated border border-slate-800/50 text-[10px] text-slate-500 grid grid-cols-2 gap-1.5">
              <div>Eff. submergence: <strong className="text-slate-200 font-mono">{fmt(R.effSubm,2)} m</strong></div>
              <div>P_bed: <strong className="text-slate-200 font-mono">{fmt((P_ATM+rho_w(spec.waterTempC)*G*R.effSubm)/1000,2)} kPa</strong></div>
              <div>Rise time: <strong className="text-slate-200 font-mono">{fmt(R.riseTimeS,1)} s</strong></div>
              <div>v_bed: <strong className="text-slate-200 font-mono">{R.v_bed.toExponential(2)} m/s</strong></div>
            </div>
          </Card>

          {/* Performance & SOTE */}
          <Card title="§4 — Performance & SOTE" icon={Wind} iconColor="text-emerald-400">
            <div className="mb-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">SOTE Mode</div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={()=>upd({soteMode:'calculated'})}
                  className={`py-2 rounded-xl text-xs font-bold border transition-all ${
                    spec.soteMode==='calculated'
                      ? 'bg-emerald-500/15 border-emerald-500/60 text-emerald-300'
                      : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                  }`}>Calculated from bubble ∅</button>
                <button onClick={()=>upd({soteMode:'manual'})}
                  className={`py-2 rounded-xl text-xs font-bold border transition-all ${
                    spec.soteMode==='manual'
                      ? 'bg-sky-500/15 border-sky-500/60 text-sky-300'
                      : 'bg-rb-elevated border-slate-700/60 text-slate-400 hover:border-slate-500'
                  }`}>Manual override</button>
              </div>
            </div>
            {spec.soteMode==='manual' && (
              <div className="mb-3">
                <NI label="SOTE per metre" unit="%/m" value={spec.manualSotePerM}
                  onChange={v=>upd({manualSotePerM:Math.max(1,Math.min(10,v))})} step={0.1} min={1} max={10}
                  tip="From manufacturer datasheet or clean-water test. Typical 5–7%/m for fine bubble."/>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <NI label="α factor" value={spec.alphaFactor}
                onChange={v=>upd({alphaFactor:Math.min(1,Math.max(0.3,v))})} step={0.01} min={0.3} max={1.0}
                tip="Process water / clean water OTE ratio. Reservoir: 0.75–0.95"/>
              <NI label="β factor" value={spec.betaFactor}
                onChange={v=>upd({betaFactor:Math.min(1,Math.max(0.85,v))})} step={0.01} min={0.85} max={1.0}
                tip="DO saturation correction for salinity/altitude. Freshwater reservoir: 0.97–0.99"/>
              <NI label="Blower efficiency" unit="fraction" value={spec.blowerEff}
                onChange={v=>upd({blowerEff:Math.min(0.95,Math.max(0.4,v))})} step={0.01} min={0.4} max={0.95}/>
              <NI label="Safety factor" unit="%" value={spec.safetyPct}
                onChange={v=>upd({safetyPct:Math.max(0,v)})} step={5} min={0} max={30}/>
            </div>
            <div className="mt-3 space-y-1.5 p-3 rounded-xl bg-rb-elevated border border-slate-800/50">
              {[
                ['SOTE/m', `${fmt(R.soteM,2)}%/m`, '#10b981'],
                ['Total SOTE', `${fmt(R.sotePct,2)}%`, '#10b981'],
                ['SOTR', `${fmt(R.modSOTR,5)} kgO₂/h`, '#f59e0b'],
                ['AOTR', `${fmt(R.modAOTR,5)} kgO₂/h`, '#f59e0b'],
                ['Blower kW', `${fmt(R.blowerKw,4)} kW`, '#ef4444'],
                ['SAE', `${fmt(R.SAE,3)} kgO₂/kWh`, R.SAE>=1.5?'#10b981':'#ef4444'],
              ].map(([label,val,col])=>(
                <div key={label as string} className="flex justify-between text-[10px]">
                  <span className="text-slate-500">{label}</span>
                  <strong className="font-mono" style={{color: col as string}}>{val}</strong>
                </div>
              ))}
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
}
